import React, { useState, useMemo } from 'react';
import {Button, message, Table} from 'antd';
import type { TableColumnsType } from 'antd';
import VehicleFormDrawer from './components/VehicleFormDrawer';
import { TmsVehicleType, TmsVehicleTypeDetail } from '../../../graphql/types/TmsVehicleType';
import FilterArea, { FilterOptions, DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import { useQuery } from '@apollo/client';
import { FIND_VEHICLE_BY_ID, FindVehicleByIdResponse, GET_VEHICLES_WITH_PAGINATION, TmsVehiclePageResponse } from './graphql/queries/getVehicles';
import { UPDATE_VEHICLE } from './graphql/mutations/vehicleMutations';
import { useMutation } from '@apollo/client';
import { GET_TMS_CARRIERS_BY_WAREHOUSE_ID } from '../../../graphql/queries/getTmsCarriers';
import { formatNumberWithCommas } from '../../../utils/numberUtils';
import { getFuelTypeLabel, FuelTypeEnum } from '../../../enums/FuelTypeEnum';
import { useTranslation } from 'react-i18next';
import { useVehicleTypes } from '../../../hooks/useVehicleTypeOptions';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import TablePaginationBar from '../../../components/TablePaginationBar';


const VehiclePage: React.FC = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [selectedVehicle, setSelectedVehicle] = useState<TmsVehicleTypeDetail | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [tempFilters, setTempFilters] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const { t, i18n } = useTranslation();
  const statusOptions = [
    { label: t('enum.activeStatus.active'), value: 1 },
    { label: t('enum.activeStatus.suspended'), value: 0 },
  ];

  const { currentWarehouse } = useWarehouseContext();
  const warehouseId = currentWarehouse?.id;

  // 构建后端筛选参数
  const buildFilters = () => {
    const filterParams: Record<string, any> = {};
    if (warehouseId) filterParams.warehouseId = Number(warehouseId);
    if (filters.licensePlate) filterParams.licensePlate = filters.licensePlate;
    if (filters.vehicleTypeId !== null) filterParams.vehicleTypeId = Number(filters.vehicleTypeId);
    if (filters.carrierId !== null) filterParams.carrierId = filters.carrierId;
    if (filters.fuelType !== null) filterParams.fuelType = filters.fuelType;
    if (filters.status !== null) filterParams.status = filters.status;
    return Object.keys(filterParams).length > 0 ? filterParams : undefined;
  };

  const { data: paginatedData, loading, refetch } = useQuery<TmsVehiclePageResponse>(
    GET_VEHICLES_WITH_PAGINATION,
    {
      variables: {
        filter: buildFilters(),
        pageRequest: {
          page: currentPage,
          size: pageSize,
        },
      },
      fetchPolicy: 'no-cache',
      skip: !warehouseId,
    }
  );

  const listingResponse = paginatedData?.getTmsVehiclesWithPagination;
  const vehicleList = listingResponse?.content ?? [];
  const totalElements = listingResponse?.totalElements ?? 0;

  // 获取承运商数据（用于筛选下拉框）
  const { data: carrierData } = useQuery(GET_TMS_CARRIERS_BY_WAREHOUSE_ID, {
    variables: { warehouseId: warehouseId ? Number(warehouseId) : null },
    skip: !warehouseId
  });

  // 查询单个车辆详情
  const { refetch: refetchVehicleDetail } = useQuery<FindVehicleByIdResponse>(
    FIND_VEHICLE_BY_ID,
    {
      variables: { id: null },
      skip: true,
      fetchPolicy: 'network-only',
    }
  );

  const handleFilter = () => {
    setFilters({ ...tempFilters });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setTempFilters(DEFAULT_FILTER_OPTIONS);
    setFilters(DEFAULT_FILTER_OPTIONS);
    setCurrentPage(1);
  };

  const showNewDrawer = () => {
    setSelectedVehicle(null);
    setDrawerMode('create');
    setDrawerVisible(true);
  };

  const showEditDrawer = async (record: TmsVehicleType) => {
    try {
      const result = await refetchVehicleDetail({ id: Number(record.id) });
      const vehicleDetail = result.data?.findTmsVehicleById;

      if (vehicleDetail) {
        setSelectedVehicle(vehicleDetail);
        setDrawerMode('edit');
        setDrawerVisible(true);
      } else {
        message.error('Vehicle detail not found for ID:', record.id);
      }
    } catch (error) {
      console.error('Failed to load vehicle details:', error);
    }
  };

  const [updateVehicle] = useMutation<Boolean>(UPDATE_VEHICLE);

  const handleDrawerSubmit = async (values: TmsVehicleTypeDetail) => {
    if (drawerMode === 'create') {
      // 新增逻辑
    } else if (drawerMode === 'edit' && selectedVehicle) {
      await updateVehicle({ variables: { input: values } });
    }
    setDrawerVisible(false);
    refetch();
  };

  // 获取车型下拉数据
  const { vehicleTypes } = useVehicleTypes();
  const modelOptions = (vehicleTypes || []).map(vt => ({
    label: vt.nameEn,
    value: vt.id,
    status: vt.status
  }));

  // 承运商选项
  const carrierOptions = useMemo(() => {
    const carriers = carrierData?.getTmsCarriersByWarehouseId;
    if (!carriers || carriers.length === 0) {
      return [];
    }
    return carriers.map((c: any) => ({ label: c.carrierName, value: Number(c.id), status: c.status }));
  }, [carrierData]);

  const columns: TableColumnsType<TmsVehicleType> = [
    {
      title: t('tms.vehicle.columns.id'),
      dataIndex: 'vehicleNumber',
      key: 'vehicleNumber',
      align: 'left' as const,
      width: 120,
      ellipsis: true,
      render: (text: string, record: TmsVehicleType) => (
        <Button type="link" onClick={() => showEditDrawer(record)} style={{ paddingLeft: 0, whiteSpace: 'nowrap' }}>
            {text}
        </Button>
      ),
    },
    {
      title: t('tms.vehicle.columns.licensePlate'),
      dataIndex: 'licensePlate',
      key: 'licensePlate',
      align: 'left' as const,
      width: 140,
      ellipsis: true,
      render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
    },
    {
      title: t('tms.vehicle.columns.carrierId'),
      dataIndex: 'carrierName',
      key: 'carrierName',
      align: 'left' as const,
      width: 160,
      ellipsis: true,
      render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
    },
    {
      title: t('tms.vehicle.columns.model'),
      dataIndex: 'vehicleTypeId',
      key: 'vehicleTypeId',
      align: 'left' as const,
      width: 140,
      ellipsis: true,
      render: (vehicleTypeId: number) => {
        const vt = vehicleTypes?.find(v => String(v.id) === String(vehicleTypeId));
        const name = vt ? vt.nameEn : undefined;
        return <span style={{ whiteSpace: 'nowrap' }}>{name || '-'}</span>;
      }
    },
    {
      title: t('tms.vehicle.columns.fuelType'),
      dataIndex: 'fuelType',
      key: 'fuelType',
      align: 'left' as const,
      width: 120,
      ellipsis: true,
      render: (value: number) => <span style={{ whiteSpace: 'nowrap' }}>{value ? getFuelTypeLabel(value as FuelTypeEnum, t) || '-' : '-'}</span>
    },
    {
      title: t('tms.vehicle.columns.lengthFt'),
      dataIndex: 'lengthFt',
      key: 'lengthFt',
      align: 'left' as const,
      width: 120,
      ellipsis: true,
      render: (value: number) => {
        const text = formatNumberWithCommas(value);
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
    {
      title: t('tms.vehicle.columns.widthFt'),
      dataIndex: 'widthFt',
      key: 'widthFt',
      align: 'left' as const,
      width: 120,
      ellipsis: true,
      render: (value: number) => {
        const text = formatNumberWithCommas(value);
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
    {
      title: t('tms.vehicle.columns.heightFt'),
      dataIndex: 'heightFt',
      key: 'heightFt',
      align: 'left' as const,
      width: 120,
      ellipsis: true,
      render: (value: number) => {
        const text = formatNumberWithCommas(value);
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
    {
      title: t('tms.vehicle.columns.maxWeightLbs'),
      dataIndex: 'maxWeightLbs',
      key: 'maxWeightLbs',
      align: 'left' as const,
      width: 140,
      ellipsis: true,
      render: (value: number) => {
        const text = formatNumberWithCommas(value);
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
    {
      title: t('tms.vehicle.columns.maxVolumeFt3'),
      dataIndex: 'maxVolumeFt3',
      key: 'maxVolumeFt3',
      align: 'left' as const,
      width: 140,
      ellipsis: true,
      render: (value: number) => {
        const text = formatNumberWithCommas(value);
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
    {
      title: t('tms.vehicle.columns.status'),
      dataIndex: 'status',
      key: 'status',
      align: 'left' as const,
      width: 120,
      ellipsis: true,
      render: (status: number) => {
        const color = status === 1 ? 'text-green-600' : 'text-gray-400';
        const text = status === 1 ? t('enum.activeStatus.active') : t('enum.activeStatus.suspended');
        return <span className={color} style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
  ];

  return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
          {/* 筛选区 */}
          <FilterArea
            carrierOptions={carrierOptions}
            modelOptions={modelOptions}
            statusOptions={statusOptions}
            filters={tempFilters}
            setFilters={setTempFilters}
          />
          {/* 批量操作区域 */}
          <div className="flex justify-end items-center mb-3 pl-0">
            <div className="flex gap-2">
              <Button
                  type="primary"
                  onClick={showNewDrawer}
                  className="w-auto min-w-24"
              >
                  <span className="font-bold">{t('tms.vehicle.buttons.new')}</span>
              </Button>
              <Button
                type="primary"
                onClick={handleFilter}
                className="w-auto min-w-24"
              >
                <span className="font-bold">{t('tms.vehicle.buttons.filter')}</span>
              </Button>
              <Button
                onClick={handleResetFilters}
                className="w-auto min-w-24"
              >
                <span className="font-bold">{t('tms.vehicle.buttons.reset')}</span>
              </Button>
            </div>
          </div>
          {/* 表格 */}
          <Table
              rowKey="id"
              loading={loading}
              columns={columns}
              dataSource={vehicleList}
              pagination={false}
              scroll={{x: 'max-content'}}
              size="middle"
              tableLayout="fixed"
           className="mb-4"/>

          {/* 分页 */}
          <TablePaginationBar
            total={totalElements}
            current={currentPage}
            pageSize={pageSize}
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size || pageSize);
            }}
          />
          {/* 新建/编辑抽屉 */}
          <VehicleFormDrawer
            visible={drawerVisible}
            onClose={() => setDrawerVisible(false)}
            onSubmit={handleDrawerSubmit}
            initialValues={selectedVehicle || undefined}
            mode={drawerMode}
            carrierOptions={carrierOptions}
            modelOptions={modelOptions}
            statusOptions={statusOptions}
          />
        </div>
  );
};

export default VehiclePage;
